import { BoxProps, chakra } from '@chakra-ui/react';
import { motion, useReducedMotion } from 'framer-motion';

const MotionBox = chakra(motion.div);

// Chakra's `transition` style prop clashes with framer-motion's, so it is left out here.
type RevealOnScrollProps = Omit<BoxProps, 'transition'> & {
  delay?: number;
  direction?: 'up' | 'left' | 'right';
};

const OFFSETS = {
  up: { x: 0, y: 32 },
  left: { x: -32, y: 0 },
  right: { x: 32, y: 0 },
};

// Fades and slides children in once they scroll into view.
// Only opacity and transform are animated, and the observer stops after the first reveal.
// Reveals as soon as any part enters the viewport (inset 10% from the bottom), so a section taller
// than the screen can never stay hidden waiting for a percentage of it to be visible.
export const RevealOnScroll: React.FC<RevealOnScrollProps> = ({
  children,
  delay = 0,
  direction = 'up',
  ...boxProps
}) => {
  const shouldReduceMotion = useReducedMotion();

  // Timing lives in variants so framer-motion's `transition` never reaches Chakra's style prop.
  const variants = {
    hidden: { opacity: 0, ...OFFSETS[direction] },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1], delay },
    },
  };

  return (
    <MotionBox
      variants={variants}
      initial={shouldReduceMotion ? false : 'hidden'}
      whileInView="visible"
      viewport={{ once: true, amount: 'some', margin: '0px 0px -10% 0px' }}
      {...boxProps}>
      {children}
    </MotionBox>
  );
};
